"use client";

import { useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Stack,
  Fade,
  Zoom,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import {
  CloudUpload,
  Delete,
  MusicNote,
  Close,
  CheckCircle,
  VideoFile,
} from "@mui/icons-material";

interface VideoFile {
  file: File;
  id: string;
}

const AUDIO_FORMATS = [
  { value: "mp3", label: "MP3", mimeType: "audio/mpeg" },
  { value: "wav", label: "WAV", mimeType: "audio/wav" },
  { value: "aac", label: "AAC", mimeType: "audio/aac" },
  { value: "ogg", label: "OGG", mimeType: "audio/ogg" },
  { value: "flac", label: "FLAC", mimeType: "audio/flac" },
];

const QUALITY_OPTIONS = [
  { value: "320", label: "High (320 kbps)" },
  { value: "256", label: "Medium-High (256 kbps)" },
  { value: "192", label: "Medium (192 kbps)" },
  { value: "128", label: "Standard (128 kbps)" },
  { value: "96", label: "Low (96 kbps)" },
];

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

export default function VideoToAudioConverter() {
  const [videoFile, setVideoFile] = useState<VideoFile | null>(null);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [audioFormat, setAudioFormat] = useState("mp3");
  const [quality, setQuality] = useState("192");

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      setError(
        `File size exceeds 100MB limit. Please select a smaller video file.`
      );
      return;
    }

    // Check if it's a video file
    if (!file.type.startsWith("video/")) {
      setError(`${file.name} is not a supported video format.`);
      return;
    }

    setVideoFile({
      file,
      id: Math.random().toString(36).substr(2, 9),
    });
    setError(null);
    setSuccess(false);
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect]
  );

  const removeFile = () => {
    setVideoFile(null);
    setError(null);
    setSuccess(false);
    const fileInput = document.getElementById(
      "video-file-input"
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleFormatChange = (event: SelectChangeEvent) => {
    setAudioFormat(event.target.value);
  };

  const handleQualityChange = (event: SelectChangeEvent) => {
    setQuality(event.target.value);
  };

  const convertToAudio = async () => {
    if (!videoFile) {
      setError("Please select a video file to convert.");
      return;
    }

    setConverting(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append("video", videoFile.file);
      formData.append("format", audioFormat);
      formData.append("quality", quality);

      const response = await axios.post(
        process.env.NEXT_PUBLIC_MEDIA_CONVERTER_API + "/video-to-audio",
        formData,
        {
          timeout: 600000,
          responseType: "blob",
        }
      );

      // Get the audio blob
      const blob = new Blob([response.data]);

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const originalName = videoFile.file.name.replace(/\.[^/.]+$/, "");
      a.download = `${originalName}.${audioFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSuccess(true);

      // Clear file after successful conversion
      setTimeout(() => {
        removeFile();
      }, 2000);
    } catch (err) {
      let errorMessage = "Failed to convert video to audio";
      if (axios.isAxiosError(err) && err.response) {
        try {
          const contentType = String(err.response.headers["content-type"] ?? "");
          if (contentType.includes("application/json")) {
            const errorText = await new Response(err.response.data).text();
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error || errorMessage;
          } else {
            errorMessage = `Server error: ${err.response.status}`;
          }
        } catch {
          errorMessage = err.message || errorMessage;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setConverting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Upload Area */}
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: "3px dashed",
            borderColor: dragActive ? "primary.main" : "grey.300",
            borderRadius: 3,
            p: 4,
            textAlign: "center",
            bgcolor: dragActive ? "primary.50" : "grey.50",
            cursor: "pointer",
            transition: "all 0.3s ease",
            position: "relative",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "primary.50",
            },
          }}
          onClick={() => document.getElementById("video-file-input")?.click()}
        >
          <input
            id="video-file-input"
            type="file"
            accept="video/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            style={{ display: "none" }}
          />

          <CloudUpload
            sx={{
              fontSize: 80,
              color: dragActive ? "primary.main" : "grey.400",
              mb: 2,
            }}
          />

          <Typography
            variant="h6"
            component="p"
            gutterBottom
            fontWeight={600}
            color="text.primary"
          >
            {dragActive
              ? "Drop your video here"
              : "Click to upload or drag and drop"}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Supported formats: MP4, AVI, MOV, MKV, WebM, and more
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Maximum file size: 100MB
          </Typography>

          <Button
            variant="contained"
            startIcon={<VideoFile sx={{ color: "#ffffff" }} />}
            sx={{
              mt: 3,
              color: "#ffffff",
              backgroundColor: "#5558d3",
              "&:hover": {
                backgroundColor: "#4448c2",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              document.getElementById("video-file-input")?.click();
            }}
          >
            Select Video
          </Button>
        </Box>

        {/* Output Options */}
        <Fade in={true}>
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="h6"
              component="p"
              fontWeight={600}
              sx={{ mb: 2 }}
            >
              Output Options
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="format-label">Audio Format</InputLabel>
                <Select
                  labelId="format-label"
                  value={audioFormat}
                  label="Audio Format"
                  onChange={handleFormatChange}
                >
                  {AUDIO_FORMATS.map((format) => (
                    <MenuItem key={format.value} value={format.value}>
                      {format.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="quality-label">Audio Quality</InputLabel>
                <Select
                  labelId="quality-label"
                  value={quality}
                  label="Audio Quality"
                  onChange={handleQualityChange}
                >
                  {QUALITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>
        </Fade>

        {/* Selected File Preview */}
        {videoFile && (
          <Fade in={!!videoFile}>
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="p" fontWeight={600}>
                  Selected Video
                </Typography>
                <Button
                  size="small"
                  startIcon={<Delete />}
                  onClick={removeFile}
                  color="error"
                  variant="outlined"
                >
                  Remove
                </Button>
              </Box>

              <Zoom in>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    bgcolor: "grey.50",
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      bgcolor: "#7B1FA2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <VideoFile sx={{ fontSize: 32, color: "white" }} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {videoFile.file.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(videoFile.file.size)}
                    </Typography>
                  </Box>

                  <IconButton
                    onClick={removeFile}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.9)",
                      "&:hover": {
                        bgcolor: "error.main",
                        color: "white",
                      },
                    }}
                  >
                    <Close />
                  </IconButton>
                </Paper>
              </Zoom>
            </Box>
          </Fade>
        )}

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mt: 3 }}
            icon={<CheckCircle />}
            onClose={() => setSuccess(false)}
          >
            Audio extracted successfully! Your download should start
            automatically.
          </Alert>
        )}

        {/* Convert Button */}
        <Box sx={{ mt: 4 }}>
          {converting && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<MusicNote sx={{ color: "#ffffff" }} />}
            onClick={convertToAudio}
            disabled={converting || !videoFile}
            style={{
              color: "#ffffff",
            }}
            sx={{
              py: 2,
              fontSize: "1.1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {converting ? "Converting..." : "Extract Audio"}
          </Button>

          {videoFile && (
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              sx={{ mt: 2 }}
            >
              <Chip
                icon={<VideoFile />}
                label="Video File"
                color="primary"
                variant="outlined"
              />
              <Chip
                label={formatFileSize(videoFile.file.size)}
                variant="outlined"
              />
              <Chip
                icon={<MusicNote />}
                label={audioFormat.toUpperCase()}
                color="secondary"
                variant="outlined"
              />
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
